import {MouseEvent, ReactNode} from "react";
import {css, cx} from "@panda/css";

import {Container} from "@pg-design/grid-module";
import {Loader} from "@pg-design/loader-module";
import {RequestState} from "@pg-mono/request-state";

import {PropertyBoxVariantBCD} from "../atoms/PropertyBox/PropertyBoxVariantBCD";
import {AmplitudeFavouriteLocation, AmplitudeFavouritePlacement} from "../favorite/tracking/get_amplitude_favourites_event_properties";
import {usePrimaryClick} from "../hooks/use_primary_click";
import {createOfferListingAmplitudePendingDetailUrl} from "../offer/list/helpers/offer_listing_amplitude_item_list_id";
import {IOfferDetail} from "../offer/types/IOfferDetail";
import {IPropertyListProperty} from "../property/types/PropertyListProperty";
import {getAmplitudeItemListContextWithStaticFallback} from "../tracking/amplitude/get_amplitude_item_list_context_with_static_fallback";
import {IAmplitudeItemListContext, IAmplitudeViewItemOffer} from "../tracking/amplitude/get_amplitude_view_item_event_properties";
import {trackPropertySelectItemWithListContext} from "../tracking/amplitude/track_property_select_item_with_list_context";
import {gtmEventPropertyListClick} from "../tracking/google_tag_manager/gtm_event_property_list";
import {useGTMEcommercePropertyListViewTracking} from "../tracking/hooks/use_gtm_ecommerce_property_list_view_tracking";
import {PropertyListLoader} from "./PropertyListLoader";

export interface IPropertyTileListProps {
    offer: IOfferDetail;
    properties: IPropertyListProperty[];
    requestState: RequestState;
    className?: string;
    gridLayoutCols?: {
        sm?: number;
        md?: number;
        lg?: number;
    };
    onPropertyTileClick?: (property: IPropertyListProperty) => void;
    propertyBoxComponent?: "div";
    renderPropertyTile?: (property: IPropertyListProperty) => ReactNode;
    amplitudeItemListContext?: IAmplitudeItemListContext | null;
    trackingMeta: {
        listId: string;
        listName: string;
    };
    favouritePlacement?: AmplitudeFavouritePlacement;
    favouriteLocation?: AmplitudeFavouriteLocation;
}

export const PropertyListTileList = (props: IPropertyTileListProps) => {
    const {
        offer,
        properties,
        requestState,
        className,
        gridLayoutCols,
        onPropertyTileClick,
        propertyBoxComponent,
        renderPropertyTile,
        amplitudeItemListContext,
        trackingMeta,
        favouritePlacement,
        favouriteLocation
    } = props;

    const {containerId} = useGTMEcommercePropertyListViewTracking({
        offer,
        properties,
        listId: trackingMeta.listId,
        listName: trackingMeta.listName,
        useVisibilityTracking: true,
        isListReady: requestState === RequestState.Success
    });
    const amplitudeSelectItemListContext = getAmplitudeItemListContextWithStaticFallback({
        dynamicItemListContext: amplitudeItemListContext,
        trackingMeta
    });
    const updatePropertyDetailLinkWithPendingContext = (event: MouseEvent, property: IPropertyListProperty) => {
        const anchor = (event.target as HTMLElement).closest("a[href]") as HTMLAnchorElement | null;

        if (!anchor) {
            return;
        }

        anchor.href = createOfferListingAmplitudePendingDetailUrl({
            url: anchor.href,
            itemId: offer.id,
            propertyId: property.id,
            itemListContext: amplitudeItemListContext
        });
    };

    const handlePropertyTileClick = usePrimaryClick((event: MouseEvent, property: IPropertyListProperty, index: number) => {
        gtmEventPropertyListClick("rzut");
        trackPropertySelectItemWithListContext({
            offer: {
                id: offer.id,
                name: offer.name,
                type: offer.type,
                currency: offer.currency,
                vendor: offer.vendor,
                region: offer.region as IAmplitudeViewItemOffer["region"]
            },
            property,
            index,
            itemListContext: amplitudeSelectItemListContext
        });

        updatePropertyDetailLinkWithPendingContext(event, property);
        onPropertyTileClick?.(property);
    });

    if (properties.length > 0) {
        return (
            <Container className={cx(tileListContainerStyle, className)} data-id={containerId}>
                <ul className={cx(propertyListPlanBoxListStyle, gridColumnsStyle(gridLayoutCols))}>
                    {properties.map((property, index) => {
                        return (
                            <li key={property.id} onMouseDown={(event) => handlePropertyTileClick(event, property, index)}>
                                <div className={tileListItemStyle}>
                                    {renderPropertyTile ? (
                                        renderPropertyTile(property)
                                    ) : (
                                        <PropertyBoxVariantBCD
                                            property={property}
                                            activeRedirectButton
                                            as={propertyBoxComponent}
                                            favouritePlacement={favouritePlacement}
                                            favouriteLocation={favouriteLocation}
                                            amplitudeItemListContext={amplitudeItemListContext}
                                            trackingMeta={trackingMeta}
                                            index={index}
                                        />
                                    )}
                                </div>
                            </li>
                        );
                    })}
                </ul>

                {requestState === RequestState.Waiting && <PropertyListLoader />}
            </Container>
        );
    } else if (requestState === RequestState.Waiting) {
        return (
            <div className={loaderWrapNoPropertiesStyle}>
                <Loader size="md" />
            </div>
        );
    } else {
        return <div className={noOffersTextStyle}>Brak ofert do wyświetlenia</div>;
    }
};

const gridColumnsStyle = (gridLayoutCols?: IPropertyTileListProps["gridLayoutCols"]) =>
    cx(
        gridColumnsBaseStyle,
        smGridColumnsStyles[gridLayoutCols?.sm || 2],
        mdGridColumnsStyles[gridLayoutCols?.md || 3],
        lgGridColumnsStyles[gridLayoutCols?.lg || 4]
    );

const tileListContainerStyle = css({
    py: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative"
});

const propertyListPlanBoxListStyle = css({
    listStyle: "none",
    display: "grid",
    columnGap: 1.5,
    rowGap: 1.5,
    mb: 3,
    px: 2,
    sm: {
        px: 5
    },
    md: {
        columnGap: 2,
        rowGap: 2
    }
});

const gridColumnsBaseStyle = css({
    gridTemplateColumns: "1fr"
});

const smGridColumnsStyles: Record<number, string> = {
    1: css({sm: {gridTemplateColumns: "repeat(1, 1fr)"}}),
    2: css({sm: {gridTemplateColumns: "repeat(2, 1fr)"}}),
    3: css({sm: {gridTemplateColumns: "repeat(3, 1fr)"}}),
    4: css({sm: {gridTemplateColumns: "repeat(4, 1fr)"}}),
    5: css({sm: {gridTemplateColumns: "repeat(5, 1fr)"}}),
    6: css({sm: {gridTemplateColumns: "repeat(6, 1fr)"}})
};

const mdGridColumnsStyles: Record<number, string> = {
    1: css({md: {gridTemplateColumns: "repeat(1, 1fr)"}}),
    2: css({md: {gridTemplateColumns: "repeat(2, 1fr)"}}),
    3: css({md: {gridTemplateColumns: "repeat(3, 1fr)"}}),
    4: css({md: {gridTemplateColumns: "repeat(4, 1fr)"}}),
    5: css({md: {gridTemplateColumns: "repeat(5, 1fr)"}}),
    6: css({md: {gridTemplateColumns: "repeat(6, 1fr)"}})
};

const lgGridColumnsStyles: Record<number, string> = {
    1: css({lg: {gridTemplateColumns: "repeat(1, 1fr)"}}),
    2: css({lg: {gridTemplateColumns: "repeat(2, 1fr)"}}),
    3: css({lg: {gridTemplateColumns: "repeat(3, 1fr)"}}),
    4: css({lg: {gridTemplateColumns: "repeat(4, 1fr)"}}),
    5: css({lg: {gridTemplateColumns: "repeat(5, 1fr)"}}),
    6: css({lg: {gridTemplateColumns: "repeat(6, 1fr)"}})
};

const tileListItemStyle = css({
    position: "relative",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    flexBasis: "100%"
});

const loaderWrapNoPropertiesStyle = css({
    maxHeight: "100%",
    maxWidth: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center"
});

const noOffersTextStyle = css({
    fontSize: "1.6rem",
    lineHeight: "2.4rem",
    padding: "1.4rem"
});
import {MouseEvent, ReactNode} from "react";
import {css, cx} from "@panda/css";

import {Container} from "@pg-design/grid-module";
import {Loader} from "@pg-design/loader-module";
import {RequestState} from "@pg-mono/request-state";

import {PropertyBoxVariantBCD} from "../atoms/PropertyBox/PropertyBoxVariantBCD";
import {AmplitudeFavouriteLocation, AmplitudeFavouritePlacement} from "../favorite/tracking/get_amplitude_favourites_event_properties";
import {usePrimaryClick} from "../hooks/use_primary_click";
import {createOfferListingAmplitudePendingDetailUrl} from "../offer/list/helpers/offer_listing_amplitude_item_list_id";
import {IOfferDetail} from "../offer/types/IOfferDetail";
import {IPropertyListProperty} from "../property/types/PropertyListProperty";
import {getAmplitudeItemListContextWithStaticFallback} from "../tracking/amplitude/get_amplitude_item_list_context_with_static_fallback";
import {IAmplitudeItemListContext, IAmplitudeViewItemOffer} from "../tracking/amplitude/get_amplitude_view_item_event_properties";
import {trackPropertySelectItemWithListContext} from "../tracking/amplitude/track_property_select_item_with_list_context";
import {gtmEventPropertyListClick} from "../tracking/google_tag_manager/gtm_event_property_list";
import {useGTMEcommercePropertyListViewTracking} from "../tracking/hooks/use_gtm_ecommerce_property_list_view_tracking";
import {PropertyListLoader} from "./PropertyListLoader";

export interface IPropertyTileListProps {
    offer: IOfferDetail;
    properties: IPropertyListProperty[];
    requestState: RequestState;
    className?: string;
    gridLayoutCols?: {
        sm?: number;
        md?: number;
        lg?: number;
    };
    onPropertyTileClick?: (property: IPropertyListProperty) => void;
    propertyBoxComponent?: "div";
    renderPropertyTile?: (property: IPropertyListProperty) => ReactNode;
    amplitudeItemListContext?: IAmplitudeItemListContext | null;
    trackingMeta: {
        listId: string;
        listName: string;
    };
    favouritePlacement?: AmplitudeFavouritePlacement;
    favouriteLocation?: AmplitudeFavouriteLocation;
}

export const PropertyListTileList = (props: IPropertyTileListProps) => {
    const {
        offer,
        properties,
        requestState,
        className,
        gridLayoutCols,
        onPropertyTileClick,
        propertyBoxComponent,
        renderPropertyTile,
        amplitudeItemListContext,
        trackingMeta,
        favouritePlacement,
        favouriteLocation
    } = props;

    const {containerId} = useGTMEcommercePropertyListViewTracking({
        offer,
        properties,
        listId: trackingMeta.listId,
        listName: trackingMeta.listName,
        useVisibilityTracking: true,
        isListReady: requestState === RequestState.Success
    });
    const amplitudeSelectItemListContext = getAmplitudeItemListContextWithStaticFallback({
        dynamicItemListContext: amplitudeItemListContext,
        trackingMeta
    });
    const updatePropertyDetailLinkWithPendingContext = (event: MouseEvent, property: IPropertyListProperty) => {
        const anchor = (event.target as HTMLElement).closest("a[href]") as HTMLAnchorElement | null;

        if (!anchor) {
            return;
        }

        anchor.href = createOfferListingAmplitudePendingDetailUrl({
            url: anchor.href,
            itemId: offer.id,
            propertyId: property.id,
            itemListContext: amplitudeItemListContext
        });
    };

    const handlePropertyTileClick = usePrimaryClick((event: MouseEvent, property: IPropertyListProperty, index: number) => {
        gtmEventPropertyListClick("rzut");
        trackPropertySelectItemWithListContext({
            offer: {
                id: offer.id,
                name: offer.name,
                type: offer.type,
                currency: offer.currency,
                vendor: offer.vendor,
                region: offer.region as IAmplitudeViewItemOffer["region"]
            },
            property,
            index,
            itemListContext: amplitudeSelectItemListContext
        });

        updatePropertyDetailLinkWithPendingContext(event, property);
        onPropertyTileClick?.(property);
    });

    if (properties.length > 0) {
        return (
            <Container className={cx(tileListContainerStyle, className)} data-id={containerId}>
                <ul className={cx(propertyListPlanBoxListStyle, gridColumnsStyle(gridLayoutCols))}>
                    {properties.map((property, index) => {
                        return (
                            <li key={property.id} onMouseDown={(event) => handlePropertyTileClick(event, property, index)}>
                                <div className={tileListItemStyle}>
                                    {renderPropertyTile ? (
                                        renderPropertyTile(property)
                                    ) : (
                                        <PropertyBoxVariantBCD
                                            property={property}
                                            activeRedirectButton
                                            as={propertyBoxComponent}
                                            favouritePlacement={favouritePlacement}
                                            favouriteLocation={favouriteLocation}
                                            amplitudeItemListContext={amplitudeItemListContext}
                                            trackingMeta={trackingMeta}
                                            index={index}
                                        />
                                    )}
                                </div>
                            </li>
                        );
                    })}
                </ul>

                {requestState === RequestState.Waiting && <PropertyListLoader />}
            </Container>
        );
    } else if (requestState === RequestState.Waiting) {
        return (
            <div className={loaderWrapNoPropertiesStyle}>
                <Loader size="md" />
            </div>
        );
    } else {
        return <div className={noOffersTextStyle}>Brak ofert do wyświetlenia</div>;
    }
};

const gridColumnsStyle = (gridLayoutCols?: IPropertyTileListProps["gridLayoutCols"]) =>
    cx(
        gridColumnsBaseStyle,
        smGridColumnsStyles[gridLayoutCols?.sm || 2],
        mdGridColumnsStyles[gridLayoutCols?.md || 3],
        lgGridColumnsStyles[gridLayoutCols?.lg || 4]
    );

const tileListContainerStyle = css({
    py: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative"
});

const propertyListPlanBoxListStyle = css({
    listStyle: "none",
    display: "grid",
    columnGap: 1.5,
    rowGap: 1.5,
    mb: 3,
    px: 2,
    sm: {
        px: 5
    },
    md: {
        columnGap: 2,
        rowGap: 2
    }
});

const gridColumnsBaseStyle = css({
    gridTemplateColumns: "1fr"
});

const smGridColumnsStyles: Record<number, string> = {
    1: css({sm: {gridTemplateColumns: "repeat(1, 1fr)"}}),
    2: css({sm: {gridTemplateColumns: "repeat(2, 1fr)"}}),
    3: css({sm: {gridTemplateColumns: "repeat(3, 1fr)"}}),
    4: css({sm: {gridTemplateColumns: "repeat(4, 1fr)"}}),
    5: css({sm: {gridTemplateColumns: "repeat(5, 1fr)"}}),
    6: css({sm: {gridTemplateColumns: "repeat(6, 1fr)"}})
};

const mdGridColumnsStyles: Record<number, string> = {
    1: css({md: {gridTemplateColumns: "repeat(1, 1fr)"}}),
    2: css({md: {gridTemplateColumns: "repeat(2, 1fr)"}}),
    3: css({md: {gridTemplateColumns: "repeat(3, 1fr)"}}),
    4: css({md: {gridTemplateColumns: "repeat(4, 1fr)"}}),
    5: css({md: {gridTemplateColumns: "repeat(5, 1fr)"}}),
    6: css({md: {gridTemplateColumns: "repeat(6, 1fr)"}})
};

const lgGridColumnsStyles: Record<number, string> = {
    1: css({lg: {gridTemplateColumns: "repeat(1, 1fr)"}}),
    2: css({lg: {gridTemplateColumns: "repeat(2, 1fr)"}}),
    3: css({lg: {gridTemplateColumns: "repeat(3, 1fr)"}}),
    4: css({lg: {gridTemplateColumns: "repeat(4, 1fr)"}}),
    5: css({lg: {gridTemplateColumns: "repeat(5, 1fr)"}}),
    6: css({lg: {gridTemplateColumns: "repeat(6, 1fr)"}})
};

const tileListItemStyle = css({
    position: "relative",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    flexBasis: "100%"
});

const loaderWrapNoPropertiesStyle = css({
    maxHeight: "100%",
    maxWidth: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center"
});

const noOffersTextStyle = css({
    fontSize: "1.6rem",
    lineHeight: "2.4rem",
    padding: "1.4rem"
});
